const lockStore = new Map();

function buildKey(symbol, timeframe) {
  return `${symbol}::${timeframe}`;
}

function normalizeMarket(market) {
  return String(market || "").toUpperCase();
}

function buildLockSignature(structure, zoneState, market) {
  return [
    normalizeMarket(market),
    structure?.direction || "NONE",
    structure?.bosCandleTime ?? "NO_BOS_TIME",
    structure?.bosPrice ?? "NO_BOS_PRICE",
    zoneState?.id || "NO_ZONE",
    zoneState?.high ?? "NO_HIGH",
    zoneState?.low ?? "NO_LOW",
  ].join(":");
}

function hasMatchingLockedStructure(locked, structure, market) {
  if (!locked || !structure?.event) {
    return false;
  }

  return (
    normalizeMarket(market) === normalizeMarket(locked.market || market) &&
    structure.direction === locked.direction &&
    structure.bosCandleTime === locked.bosCandleTime &&
    structure.bosPrice === locked.bosPrice
  );
}

export function resetBOSState({ symbol, timeframe }) {
  lockStore.delete(buildKey(symbol, timeframe));
}

export function updateBOSState({
  structure,
  zoneState,
  narrative,
  market,
  symbol,
  timeframe,
}) {
  const key = buildKey(symbol, timeframe);
  const entry = lockStore.get(key) || { locked: null, lockedSignature: null };
  const normalizedMarket = normalizeMarket(market);

  if (!structure?.event) {
    lockStore.delete(key);
    return { state: "WAITING", reason: "Waiting for BOS" };
  }

  if (hasMatchingLockedStructure(entry.locked, structure, normalizedMarket)) {
    return { state: "LOCKED", ...entry.locked };
  }

  const nextSignature = buildLockSignature(structure, zoneState, normalizedMarket);

  if (entry.locked && entry.lockedSignature === nextSignature) {
    return { state: "LOCKED", ...entry.locked };
  }

  if (!zoneState?.valid) {
    lockStore.delete(key);
    return { state: "WAITING", reason: "Waiting for valid zone" };
  }

  const correctMarket =
    (normalizedMarket === "BOOM" &&
      structure.direction === "BULLISH" &&
      zoneState.type === "DEMAND") ||
    (normalizedMarket === "CRASH" &&
      structure.direction === "BEARISH" &&
      zoneState.type === "SUPPLY");

  if (!correctMarket) {
    lockStore.delete(key);
    return { state: "WAITING", reason: "Zone/BOS mismatch" };
  }

  if (!narrative?.valid || narrative?.state !== "CONFIRMED") {
    lockStore.delete(key);
    return {
      state: "WAITING",
      reason: narrative?.reason || "Waiting for BOS + Zone + Narrative",
    };
  }

  const locked = {
    market: normalizedMarket,
    direction: structure.direction,
    bosPrice: structure.bosPrice,
    bosIndex: structure.bosCandleIndex,
    bosCandleIndex: structure.bosCandleIndex,
    bosCandleTime: structure.bosCandleTime,
    swingIndex: structure.swingIndex,
    zone: {
      ...zoneState,
      range: {
        high: zoneState.high,
        low: zoneState.low,
      },
    },
    reason: "BOS locked with valid narrative",
  };

  lockStore.set(key, { locked, lockedSignature: nextSignature });

  return { state: "LOCKED", ...locked };
}
